import { User, CreditCard, LifeBuoy, ChevronRight, LogOut, Bell, Gift } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from '../../context/AppStateContext';
import { useAuth } from '../../context/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import ProfileInfo from '../menu/ProfileInfo';
import PaymentWallet from '../menu/PaymentWallet';
import SupportCenter from '../menu/SupportCenter';

export default function MenuTab() {
  const navigate = useNavigate();
  const { menuScreen, setMenuScreen, profileForm, setShowNotifications, unreadCount } = useAppState();
  const { logout, user, isGuestMode, setShowGuestPrompt } = useAuth();
  const { isDesktop } = useResponsive();

  // Get time-based greeting (matches HomeTab)
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };
  const userFirstName = user?.first_name || profileForm?.firstName || 'there';

  // Get user display info - prefer API data, fallback to profileForm
  // Check verification_status field (API returns "VERIFIED", "UNVERIFIED", "PENDING", etc.)
  const getVerificationStatus = () => {
    const status = user?.verification_status || user?.verificationStatus;
    if (status === 'VERIFIED') return true;
    if (status === 'UNVERIFIED' || status === 'PENDING') return false;
    // Fallback to boolean fields
    return user?.isVerified || user?.is_verified || user?.verified || false;
  };

  const displayName = {
    firstName: user?.first_name || user?.firstName || profileForm.firstName || 'User',
    lastName: user?.last_name || user?.lastName || profileForm.lastName || '',
    email: user?.email || profileForm.email || '',
    bonndleTag: user?.bold_tag || user?.bonndleTag || user?.boldtag || user?.boldTag || profileForm.bonndleTag || '',
    joinedAt: user?.created_at || user?.createdAt || user?.joinedAt || '',
    isVerified: getVerificationStatus(),
  };

  // Format join date
  const formatJoinDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Render sub-screens
  if (menuScreen === 'profile') {
    return <ProfileInfo onBack={() => setMenuScreen(null)} />;
  }
  if (menuScreen === 'payment') {
    return <PaymentWallet onBack={() => setMenuScreen(null)} />;
  }
  if (menuScreen === 'support') {
    return <SupportCenter onBack={() => setMenuScreen(null)} />;
  }

  const menuItems = [
    { id: 'profile', icon: User, label: 'Profile Information', desc: 'Manage your personal details' },
    { id: 'payment', icon: CreditCard, label: 'Payment & Wallet', desc: 'Cards, balance & transactions' },
    { id: 'support', icon: LifeBuoy, label: 'Support Center', desc: 'FAQs, chat & contact' },
  ];

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto scrollbar-hide">
        {/* Mobile Header - matches HomeTab */}
        {!isDesktop && (
          <header className="sticky top-0 bg-white z-10 px-4 pb-3 flex items-center justify-between border-b border-gray-100 pt-[calc(0.75rem+env(safe-area-inset-top))]">
            <div className="flex items-center gap-3">
              <img src="/logo/bonndleLogoIcon_Indigo.svg" alt="Bonndle" className="h-9 w-9" />
              <h1 className="font-bold text-gray-900 text-lg">
                {getGreeting()}, {userFirstName}
              </h1>
            </div>
            <button
              onClick={() => setShowNotifications(true)}
              className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-full"
            >
              <Bell size={22} />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-green-500 rounded-full" />
              )}
            </button>
          </header>
        )}
        <div className={`px-4 py-6 ${isDesktop ? 'max-w-4xl mx-auto' : ''}`}>
        {/* Profile Card */}
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-6">
          <div className="flex items-center gap-4">
            {/* Avatar with gradient border */}
            <div className="w-16 h-16 rounded-full p-[3px] bg-gradient-to-br from-sky-400 via-blue-500 to-indigo-600">
              <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                <span className="text-xl font-bold text-indigo-700">
                  {displayName.firstName.charAt(0)}{displayName.lastName.charAt(0)}
                </span>
              </div>
            </div>
            <div className="flex-1">
              <p className="text-lg font-bold text-gray-900">
                {displayName.firstName} {displayName.lastName}
              </p>
              {(displayName.bonndleTag || displayName.joinedAt) && (
                <p className="text-xs text-gray-400 mt-1">
                  {displayName.bonndleTag && <span>@{displayName.bonndleTag.replace(/^@/, '')}</span>}
                  {displayName.bonndleTag && displayName.joinedAt && <span> | </span>}
                  {displayName.joinedAt && <span>Joined {formatJoinDate(displayName.joinedAt)}</span>}
                </p>
              )}
              {displayName.isVerified !== undefined && (
                displayName.isVerified ? (
                  <span className="inline-flex items-center mt-2 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    Verified
                  </span>
                ) : (
                  <button
                    onClick={() => navigate('/verify-identity', { state: { fromMenu: true } })}
                    className="inline-flex items-center mt-2 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors cursor-pointer"
                  >
                    Verification Pending
                  </button>
                )
              )}
            </div>
          </div>
        </div>

        {/* Referrals Card */}
        <button
          onClick={() => {
            if (isGuestMode) {
              setShowGuestPrompt(true);
              return;
            }
            navigate('/referrals');
          }}
          className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-4 mb-4 text-left hover:from-indigo-700 hover:to-purple-700 transition-all active:scale-[0.99]"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Gift className="text-white" size={20} />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-white">Refer & Earn</p>
              <p className="text-sm text-white/80">Invite friends, earn $10 each</p>
            </div>
            <ChevronRight size={20} className="text-white/80" />
          </div>
        </button>

        {/* Menu Items */}
        <div className="bg-white rounded-2xl shadow-sm divide-y divide-gray-100 mb-6">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => {
                  // Guest users can access support, but need to sign up for other screens
                  if (isGuestMode && item.id !== 'support') {
                    setShowGuestPrompt(true);
                    return;
                  }
                  setMenuScreen(item.id);
                }}
                className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors text-left"
              >
                <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
                  <Icon className="text-indigo-600" size={20} />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">{item.label}</p>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </div>
                <ChevronRight size={20} className="text-gray-400" />
              </button>
            );
          })}
        </div>

        {/* Logout/Sign Up Button */}
        {isGuestMode ? (
          <button
            onClick={() => setShowGuestPrompt(true)}
            className="w-full flex items-center justify-center gap-2 p-4 bg-indigo-600 text-white rounded-2xl shadow-sm hover:bg-indigo-700 transition-colors"
          >
            <User size={20} />
            <span className="font-semibold">Create Account</span>
          </button>
        ) : (
          <button
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 p-4 bg-white text-red-600 rounded-2xl shadow-sm hover:bg-red-600 hover:text-white transition-colors"
          >
            <LogOut size={20} />
            <span className="font-semibold">Log Out</span>
          </button>
        )}
        </div>
      </div>
    </div>
  );
}
